import { useEffect, useState } from "react";
import { Plus, X, Trophy, Crown, Star, Award, Banknote, Calendar, Flag } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import "../admin-affiliate-premium.css";

const BLANK = {
  name: "", description: "", start_at: "", end_at: "", metric: "net_loss",
  prize_pool: 0, prizes: [{ rank: 1, prize: 5000 }, { rank: 2, prize: 3000 }, { rank: 3, prize: 1500 }],
  target_tier: "",
};

export default function AffiliateContests() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);

  const load = async () => {
    try { const r = await api.get("/admin/aff-contests"); setRows(r.data?.rows || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = {
        ...form,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : new Date().toISOString(),
        end_at: form.end_at ? new Date(form.end_at).toISOString() : new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
        prize_pool: Number(form.prize_pool || 0),
      };
      await api.post("/admin/aff-contests", body); toast.success("Contest created");
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const finalize = async (id) => {
    if (!window.confirm("Finalize this contest now? Winners will be locked in.")) return;
    try {
      const r = await api.post(`/admin/aff-contests/${id}/finalize`);
      toast.success(`Finalized — ${(r.data?.winners || []).length} winner(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updPrize = (i, key, val) => {
    const p = [...form.prizes];
    p[i] = { ...p[i], [key]: Number(val) };
    setForm({ ...form, prizes: p });
  };
  const addPrize = () => setForm({ ...form, prizes: [...form.prizes, { rank: form.prizes.length + 1, prize: 0 }] });
  const rmPrize = (i) => setForm({ ...form, prizes: form.prizes.filter((_, ii) => ii !== i) });

  return (
    <div className="ap-bg p-4 lg:p-6">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold mb-1">COMPETITION</div>
          <div className="text-3xl font-black ap-gold-text">Affiliate Contests</div>
          <div className="text-white/55 text-sm mt-1">Run prize-pool competitions to boost affiliate performance.</div>
        </div>
        <button onClick={() => setOpen(true)} className="ap-btn-gold inline-flex items-center gap-2 px-4 h-10 rounded-xl text-[12px] font-bold" data-testid="new-contest-btn">
          <Plus size={14} /> New Contest
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows.length === 0 ? (
          <div className="ap-glass p-10 text-center text-white/45 lg:col-span-2">No contests yet.</div>
        ) : rows.map((c) => (
          <div key={c.id} className="ap-glass" style={{ padding: 18, border: `1px solid ${c.status === "active" ? "rgba(16,185,129,0.45)" : "rgba(212,175,55,0.35)"}` }}>
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={16} className="text-amber-300" />
              <div className="font-black text-white">{c.name}</div>
              <span className="ml-auto text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-md" style={c.status === "active" ? { color: "#6ee7b7", background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)" } : { color: "#cbd5e1", background: "rgba(148,163,184,0.10)", border: "1px solid rgba(148,163,184,0.32)" }}>{c.status}</span>
            </div>
            <div className="text-white/55 text-[12.5px] mb-2">{c.description}</div>
            <div className="grid grid-cols-3 gap-2 text-[11.5px] mb-2">
              <Info icon={Calendar} label="Start" value={(c.start_at || "").slice(0, 10)} />
              <Info icon={Flag} label="End" value={(c.end_at || "").slice(0, 10)} />
              <Info icon={Banknote} label="Pool" value={`৳${(c.prize_pool || 0).toLocaleString()}`} />
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(c.prizes || []).map((p) => (
                <span key={p.rank} className="px-2 py-0.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.35)", color: "#f4e9a1" }}>#{p.rank} → ৳{Number(p.prize || 0).toLocaleString()}</span>
              ))}
            </div>
            {(c.winners || []).length > 0 && (
              <div className="text-[12px] text-white/70 mb-2">Winners: {c.winners.map((w) => `#${w.rank} ${w.username}`).join(", ")}</div>
            )}
            {c.status === "active" && (
              <button onClick={() => finalize(c.id)} className="ap-btn-gold mt-1 px-3 h-9 rounded-lg text-[11px] font-bold" data-testid={`finalize-${c.id}`}>Finalize now</button>
            )}
          </div>
        ))}
      </div>

      {open && (
        <div className="ap-modal-overlay" onClick={() => setOpen(false)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold">NEW CONTEST</div>
              <button onClick={() => setOpen(false)} className="ap-btn-ghost p-2 rounded-lg"><X size={14} /></button>
            </div>
            <h3 className="text-xl font-black ap-gold-text mb-3">Launch Contest</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ap-input" /></Field>
              <Field label="Metric">
                <select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} className="ap-input">
                  {["net_loss", "deposits", "new_players", "conversions"].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Start at"><input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="ap-input" /></Field>
              <Field label="End at"><input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="ap-input" /></Field>
              <Field label="Prize Pool (৳)"><input type="number" value={form.prize_pool} onChange={(e) => setForm({ ...form, prize_pool: e.target.value })} className="ap-input" /></Field>
              <Field label="Target Tier (optional)"><input value={form.target_tier} onChange={(e) => setForm({ ...form, target_tier: e.target.value })} className="ap-input" /></Field>
              <div className="md:col-span-2">
                <Field label="Description"><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ap-input resize-none" /></Field>
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-2">Prizes</div>
                {form.prizes.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                    <input type="number" value={p.rank} onChange={(e) => updPrize(i, "rank", e.target.value)} placeholder="Rank" className="ap-input col-span-2" />
                    <input type="number" value={p.prize} onChange={(e) => updPrize(i, "prize", e.target.value)} placeholder="Prize ৳" className="ap-input col-span-8" />
                    <button onClick={() => rmPrize(i)} className="ap-btn-ghost rounded-lg col-span-2"><X size={14} className="mx-auto" /></button>
                  </div>
                ))}
                <button onClick={addPrize} className="ap-btn-ghost px-3 h-9 rounded-lg text-[11px] font-bold inline-flex items-center gap-1.5"><Plus size={12} /> Add prize</button>
              </div>
            </div>
            <button onClick={save} className="ap-btn-gold mt-4 px-5 h-11 rounded-xl text-[13px] font-bold inline-flex items-center gap-2" data-testid="save-contest-btn">
              <Trophy size={14} /> Launch Contest
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) { return <div><div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-1.5">{label}</div>{children}</div>; }
function Info({ icon: Icon, label, value }) {
  return <div className="p-2 rounded-lg flex items-center gap-2" style={{ background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.06)" }}><Icon size={12} className="text-amber-300" /><div><div className="text-[9.5px] uppercase tracking-[0.18em] text-white/45 font-bold">{label}</div><div className="text-white font-bold text-[12px]">{value}</div></div></div>;
}
